import React, { useEffect, useState } from 'react';

import { Web } from '@mui/icons-material';
import { CSVLink } from 'react-csv';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import InvoiceTable, { invoiceColumns, invoiceColumnsCostumer } from './InvoiceTable';
import Excel from '../../../assets/excel.png';
import PDF from '../../../assets/pdf.png';
import { Breadcrumbs, Button, EdgeReportingWindow, SubHeader, ReportLoader } from '../../../components';
import ImageColorChanger from '../../../utils/imageColorChanger';
import getCSVData from '../csv';
import { userHasAccess, generateReport, getReportData, removeReportData } from '../operations';
import CheckPDFForm from './CheckPDFForm';
import getEdgeReportingMenu from '../getEdgeReportingMenu';

import './styles.scoped.css';

const InvoicePdf = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { menuItems } = getEdgeReportingMenu(t);
  const { user } = useSelector(state => state.admin);
  const [data, setData] = useState([]);
  const [form, setForm] = useState({});
  const [loadingResults, setLoadingResults] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [isCustomer, setIsCustomer] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  useEffect(() => {
    if (menuItems) {
      if (!userHasAccess(user, menuItems.find(i => i.to === window.location.pathname)?.id)) navigate('/');
    }
  }, [menuItems, navigate, user]);

  const handleExportPDF = async () => {
    setLoadingResults(true);
    const id = isCustomer ? selectedInvoice?.Invoice_ID : data[0].Invoice_ID;
    if (isCustomer && !selectedInvoice) {
      alert('Please select an invoice');
      return;
    }
    const response = await generateReport({
      endpoint: 'invoice-pdf',
      payload: { invoiceId: `${id}` },
    });
    if (response.location && response.location !== '') {
      const reportData = await getReportData(response.location);
      if (reportData?.pdfBase64) {
        await removeReportData(response.location);
        let blob = null;
        blob = b64toBlob(reportData?.pdfBase64, 'application/pdf');
        const blobURL = URL.createObjectURL(blob);
        const newWindowViewer = window.open(blobURL);
        const theDoc = newWindowViewer.document;
        const theScript = theDoc.createElement('script');
        function injectThis() {
          window.print();
        }
        theScript.innerHTML = `window.onload = ${injectThis.toString()};`;
        theDoc.body.appendChild(theScript);
      } else {
        alert('PDF not available for this invoice');
        return;
      }
      setLoadingResults(false);
    }
  };

  const b64toBlob = (content, contentType) => {
    contentType = contentType || '';
    const sliceSize = 512;
    // method which converts base64 to binary
    const byteCharacters = window.atob(content);

    const byteArrays = [];
    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }
    const blob = new Blob(byteArrays, {
      type: contentType,
    }); // statement which creates the blob
    return blob;
  };

  const setSelectedRow = check => {
    setSelectedInvoice(check);
  };

  return (
    <>
      <SubHeader
        text="EDGE Reporting"
        buttons={[{ icon: <Web sx={{ color: '#fff', fontSize: 30 }} />, onPress: () => navigate('/dashboard') }]}
      />
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '30px',
        }}>
        <Breadcrumbs
          leftTitle="Reporting Home"
          middleTitle={showResults && 'Invoice PDF'}
          midItemAction={() => setShowResults(false)}
          rightTitle={showResults ? `Viewing Report ${isCustomer ? '(Customer)' : '(Invoice)'}` : 'Invoice PDF'}
          route="/edge-reporting"
        />
        {data?.length && showResults ? (
          <span>
            <img
              onClick={handleExportPDF}
              src={PDF}
              alt="export-to-pdf"
              style={{ padding: '20px 30px 0 0 ', cursor: 'pointer' }}
            />
            <CSVLink
              filename={`edge-reporting-invoice-${isCustomer ? selectedInvoice?.Invoice_ID : data[0].Invoice_ID}.csv`}
              data={getCSVData(data, Object.keys(data[0]).length < 21 ? invoiceColumnsCostumer : invoiceColumns)}>
              <ImageColorChanger
                src={Excel}
                style={{ padding: '20px 30px 0 0 ', cursor: 'pointer' }}
                alt="export-to-excel"
              />
            </CSVLink>
          </span>
        ) : null}
      </div>
      {loadingResults && <ReportLoader didAbort={() => setLoadingResults(false)} />}
      {showResults ? (
        <>
          <InvoiceTable data={data} loading={loadingResults} rowSelected={setSelectedRow} isCustomer={isCustomer} />
          <div className="buttons-row">
            <Button variant="primary" onPress={() => setShowResults(false)} style={{ marginRight: '20px', width: '100px' }} text="Back" />

            <Button
              style={{ width: '100px' }}
              onPress={() => navigate('/edge-reporting')}
              variant="outline"
              text="Cancel"
            />
          </div>
        </>
      ) : (
        <EdgeReportingWindow
          containerStyle={{
            border: '2px solid #343a40',
          }}
          windowContent={
            <CheckPDFForm
              form={form}
              setForm={setForm}
              setLoadingResults={setLoadingResults}
              isCustomer={isCustomer}
              setData={setData}
              setShowResults={setShowResults}
              setIsCustomer={setIsCustomer}
            />
          }
          windowTitle="Criteria"
          headerStyle={{ backgroundColor: '#343a40' }}
        />
      )}
    </>
  );
};

export default InvoicePdf;
